import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Calendar, Sparkles, ChevronRight, Loader2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const MOOD_LABELS = ["Rough", "Meh", "Fine", "Good", "On fire"];

const WeeklyCheckInCard = () => {
  const { access } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("mood"); // mood -> challenge -> highlight -> result
  const [mood, setMood] = useState(0);
  const [challenge, setChallenge] = useState("");
  const [highlight, setHighlight] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [recs, setRecs] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/weekly-checkin");
      setStatus(r.data);
      if (r.data.existing) setRecs(r.data.existing.recommendations);
    } catch {}
  };

  useEffect(() => {
    if (access && !access.has_access) return;
    load();
  }, [access]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/weekly-checkin", { mood, challenge, highlight });
      setRecs(r.data.recommendations);
      setStep("result");
      toast.success("Check-in saved. Here's your week.");
      load();
    } catch { toast.error("Could not save check-in"); }
    setSubmitting(false);
  };

  if (!status) return null;

  // Already filled this week — show the recap card
  if (status.existing && recs) {
    return (
      <div className="card-surface p-7" data-testid="weekly-checkin-recap">
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-amber-500 flex items-center gap-2"><Calendar className="w-3.5 h-3.5"/> YOUR WEEK · {status.week_key}</div>
          <span className="text-xs text-neutral-500 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-amber-400"/> Checked in</span>
        </div>
        <h3 className="font-serif text-2xl mb-2">This week's focus</h3>
        <p className="text-amber-400 italic mb-3">{recs.challenge_label}</p>
        <p className="text-neutral-300 leading-relaxed mb-5">{recs.tip}</p>
        <RecommendationsGrid recs={recs} />
      </div>
    );
  }

  // Prompt available
  if (!status.prompt_available && !status.existing) {
    return null; // No prompt and no existing — nothing to show
  }

  if (!open) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card-surface p-7 flex items-center justify-between gap-4 border-amber-500/30"
        data-testid="weekly-checkin-prompt"
      >
        <div className="flex-1">
          <div className="overline text-amber-500 mb-2 flex items-center gap-2"><Calendar className="w-3.5 h-3.5"/> MONDAY CHECK-IN</div>
          <h3 className="font-serif text-2xl mb-1">How did your dating week go?</h3>
          <p className="text-neutral-400 text-sm">Takes 20 seconds. We'll surface the exact lessons for your week.</p>
        </div>
        <button onClick={() => { setOpen(true); setStep("mood"); }} className="btn-amber" data-testid="start-checkin-btn">
          Start check-in
        </button>
      </motion.div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-8" data-testid="checkin-modal">
      <AnimatePresence mode="wait">
        {step === "mood" && (
          <motion.div key="mood" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="overline text-amber-500 mb-3">STEP 1 OF 3</div>
            <h3 className="font-serif text-3xl mb-6">How did it go, overall?</h3>
            <div className="grid grid-cols-5 gap-2">
              {MOOD_LABELS.map((m, i) => {
                const v = i + 1;
                return (
                  <button
                    key={v}
                    onClick={() => setMood(v)}
                    data-testid={`mood-${v}`}
                    className={`py-6 rounded-xl border transition-colors ${
                      mood === v ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:text-white"
                    }`}
                  >
                    <div className="text-2xl font-serif">{v}</div>
                    <div className="text-xs mt-1">{m}</div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setOpen(false)} className="text-sm text-neutral-500 hover:text-white px-3">Cancel</button>
              <button disabled={!mood} onClick={() => setStep("challenge")} className="btn-amber inline-flex items-center gap-2 disabled:opacity-50" data-testid="checkin-next-1">
                Next <ChevronRight className="w-4 h-4"/>
              </button>
            </div>
          </motion.div>
        )}

        {step === "challenge" && (
          <motion.div key="chal" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="overline text-amber-500 mb-3">STEP 2 OF 3</div>
            <h3 className="font-serif text-3xl mb-6">What was the biggest moment or challenge?</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {status.options.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setChallenge(o.key)}
                  data-testid={`challenge-${o.key}`}
                  className={`text-left px-5 py-4 rounded-xl border transition-colors ${
                    challenge === o.key ? "border-amber-500 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-300 hover:text-white"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setStep("mood")} className="text-sm text-neutral-500 hover:text-white px-3">Back</button>
              <button disabled={!challenge} onClick={() => setStep("highlight")} className="btn-amber inline-flex items-center gap-2 disabled:opacity-50" data-testid="checkin-next-2">
                Next <ChevronRight className="w-4 h-4"/>
              </button>
            </div>
          </motion.div>
        )}

        {step === "highlight" && (
          <motion.div key="hl" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="overline text-amber-500 mb-3">STEP 3 OF 3</div>
            <h3 className="font-serif text-3xl mb-3">One line to remember the week (optional).</h3>
            <p className="text-neutral-400 text-sm mb-5">A win, a lesson, something you want to try differently.</p>
            <textarea
              rows={3}
              value={highlight}
              onChange={(e) => setHighlight(e.target.value)}
              placeholder="e.g. Finally asked her out — Thursday 8pm, wine bar."
              className="w-full px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
              data-testid="checkin-highlight-input"
            />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setStep("challenge")} className="text-sm text-neutral-500 hover:text-white px-3">Back</button>
              <button disabled={submitting} onClick={submit} className="btn-amber inline-flex items-center gap-2 disabled:opacity-50" data-testid="checkin-submit">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin"/> Saving</> : <>Save & see my week <Sparkles className="w-4 h-4"/></>}
              </button>
            </div>
          </motion.div>
        )}

        {step === "result" && recs && (
          <motion.div key="result" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>
            <div className="overline text-amber-500 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5"/> YOUR WEEK · {status.week_key}</div>
            <h3 className="font-serif text-3xl mb-2">This week's focus</h3>
            <p className="text-amber-400 italic mb-3">{recs.challenge_label}</p>
            <p className="text-neutral-300 leading-relaxed mb-6">{recs.tip}</p>
            <RecommendationsGrid recs={recs} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const RecommendationsGrid = ({ recs }) => (
  <div className="grid md:grid-cols-2 gap-4">
    {recs.lessons?.map((l) => (
      <div key={l.id} className="card-elevated p-5" data-testid={`rec-lesson-${l.id}`}>
        <div className="overline text-neutral-500 mb-1">LESSON · {l.category}</div>
        <div className="font-serif text-lg">{l.title}</div>
        <p className="text-neutral-400 text-sm mt-2 line-clamp-3">{l.body}</p>
      </div>
    ))}
    {recs.articles?.map((a) => (
      <Link key={a.id} to={`/article/${a.id}`} className="card-elevated p-5 hover-lift block" data-testid={`rec-article-${a.id}`}>
        <div className="overline text-amber-500 mb-1">READ · {a.category}</div>
        <div className="font-serif text-lg">{a.title}</div>
        <p className="text-neutral-400 text-sm mt-2 italic line-clamp-3">{a.excerpt}</p>
      </Link>
    ))}
  </div>
);

export default WeeklyCheckInCard;
